/**
 * FAQs — tira de categorías scrollable que conmuta paneles.
 *
 * Las categorías son secciones `faq-category` hermanas de la sección `faq`, y
 * las pestañas se construyen aquí a partir de ellas: así el orden y los
 * títulos salen de la propia plantilla y no hay una lista duplicada que
 * mantener en sync.
 *
 * El acordeón de cada panel lo resuelve el custom element `collapsible-section`
 * (assets/base.js) vía `data-accordion-set`, así que de él solo nos ocupamos
 * del anclado al abrir (ver más abajo); lo demás es la selección de categoría
 * y el scroll horizontal de la tira.
 */
if (!customElements.get("faq-tabs")) {
  customElements.define(
    "faq-tabs",
    class FaqTabs extends HTMLElement {
      /** Debe cuadrar con la duración de `brn-faq-item-out` en faq.css */
      static LEAVE_MS = 180;

      connectedCallback() {
        this.tabsWrapper = this.querySelector(".brn-faq__tabs");
        this.list = this.querySelector(".brn-faq__tabs-list");
        this.arrow = this.querySelector(".brn-faq__tabs-arrow");

        if (!this.list) return;

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.updateOverflow = this.updateOverflow.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.build = this.build.bind(this);

        this.addEventListener("click", this.handleClick);
        this.list.addEventListener("keydown", this.handleKeydown);
        this.list.addEventListener("scroll", this.updateOverflow, { passive: true });
        window.addEventListener("resize", this.handleResize);

        // En el editor de temas se añaden, quitan y reordenan categorías en vivo
        document.addEventListener("shopify:section:load", this.build);
        document.addEventListener("shopify:section:unload", this.build);
        document.addEventListener("shopify:section:reorder", this.build);

        this.build();
      }

      disconnectedCallback() {
        clearTimeout(this.swapTimer);
        this.removeEventListener("click", this.handleClick);
        this.list?.removeEventListener("keydown", this.handleKeydown);
        this.list?.removeEventListener("scroll", this.updateOverflow);
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("load", this.measure);
        document.removeEventListener("shopify:section:load", this.build);
        document.removeEventListener("shopify:section:unload", this.build);
        document.removeEventListener("shopify:section:reorder", this.build);
      }

      /** (Re)construye las pestañas a partir de las secciones de categoría. */
      build() {
        this.panels = Array.from(document.querySelectorAll("[data-faq-category]"));

        if (!this.panels.length) {
          this.list.replaceChildren();
          this.tabs = [];
          this.hidden = true;
          return;
        }

        this.hidden = false;

        const previous = this.tabs?.findIndex(
          (tab) => tab.getAttribute("aria-selected") === "true",
        );

        this.tabs = this.panels.map((panel, index) => {
          const tab = document.createElement("button");
          tab.type = "button";
          tab.className = "brn-faq__tab";
          tab.setAttribute("role", "tab");
          tab.textContent = panel.dataset.title || "";

          const id = panel.dataset.sectionId || String(index);
          tab.id = `FaqTab-${id}`;
          panel.setAttribute("aria-labelledby", tab.id);
          panel.setAttribute("role", "tabpanel");
          if (!panel.id) panel.id = `FaqPanel-${id}`;
          tab.setAttribute("aria-controls", panel.id);

          return tab;
        });

        this.list.replaceChildren(...this.tabs);

        const initial = previous > -1 && previous < this.tabs.length ? previous : 0;
        this.select(initial, { animate: false });
        this.updateOverflow();
      }

      handleResize() {
        this.updateOverflow();
      }


      handleClick(event) {
        const arrow = event.target.closest(".brn-faq__tabs-arrow");
        if (arrow && this.contains(arrow)) {
          this.scrollTabs();
          return;
        }

        const tab = event.target.closest(".brn-faq__tab");
        if (!tab || !this.contains(tab)) return;

        this.select(this.tabs.indexOf(tab));
      }

      handleKeydown(event) {
        const current = this.tabs.indexOf(document.activeElement);
        if (current === -1) return;

        const keys = {
          ArrowRight: current + 1,
          ArrowLeft: current - 1,
          Home: 0,
          End: this.tabs.length - 1,
        };

        if (!(event.key in keys)) return;

        event.preventDefault();
        const next = Math.max(0, Math.min(this.tabs.length - 1, keys[event.key]));
        this.select(next);
        this.tabs[next].focus();
      }

      select(index, options) {
        const animate = !options || options.animate !== false;
        const target = this.panels[index];
        if (!target) return;

        this.tabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.setAttribute("aria-selected", isActive ? "true" : "false");
          tab.setAttribute("tabindex", isActive ? "0" : "-1");
          tab.classList.toggle("brn-faq__tab--active", isActive);
        });

        this.scrollTabIntoView(this.tabs[index], animate);

        const current = this.panels.find((panel) => !panel.hasAttribute("hidden"));
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        clearTimeout(this.swapTimer);

        // Sin animación de salida no hay nada que esperar: se conmuta y listo
        if (!animate || reduceMotion || !current || current === target) {
          this.showPanel(target, animate && !reduceMotion);
          return;
        }

        // La categoría que sale se desvanece antes de que entre la nueva; las
        // dos son secciones apiladas, así que no pueden solaparse en layout.
        current.classList.remove("brn-faq__panel--enter");
        current.classList.add("brn-faq__panel--leave");

        this.swapTimer = setTimeout(() => {
          this.showPanel(target, true);
        }, FaqTabs.LEAVE_MS);
      }

      showPanel(target, animate) {
        this.panels.forEach((panel) => {
          const isTarget = panel === target;

          panel.classList.remove("brn-faq__panel--leave", "brn-faq__panel--enter");
          panel.toggleAttribute("hidden", !isTarget);

          if (isTarget && animate) {
            // Reinicia la cascada de entrada en cada cambio de categoría
            void panel.offsetWidth;
            panel.classList.add("brn-faq__panel--enter");
          }
        });
      }

      scrollTabIntoView(tab, animate) {
        if (!tab) return;

        const listRect = this.list.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        let delta = 0;

        if (tabRect.left < listRect.left) {
          delta = tabRect.left - listRect.left;
        } else if (tabRect.right > listRect.right) {
          delta = tabRect.right - listRect.right;
        }

        if (delta === 0) return;

        this.list.scrollBy({ left: delta, behavior: animate ? "smooth" : "auto" });
      }

      scrollTabs() {
        const amount = this.list.clientWidth * 0.8;
        const direction = this.tabsWrapper.dataset.direction === "prev" ? -1 : 1;

        this.list.scrollBy({ left: amount * direction, behavior: "smooth" });
      }

      updateOverflow() {
        if (!this.list) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.list;
        const hasOverflow = scrollWidth - clientWidth > 1;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

        this.tabsWrapper.classList.toggle("brn-faq__tabs--static", !hasOverflow);
        this.tabsWrapper.dataset.direction = atEnd ? "prev" : "next";

        if (this.arrow) {
          this.arrow.setAttribute(
            "aria-label",
            atEnd ? this.dataset.labelPrev || "" : this.dataset.labelNext || "",
          );
        }
      }
    },
  );

  /* --- Anclado al abrir una pregunta ---------------------------------------
     Si la pregunta está muy abajo, la respuesta que se despliega se sale del
     área visible y parece que no ha pasado nada. Cuando eso va a ocurrir se
     recoloca centrada, empezando el desplazamiento a la vez que la expansión
     para que se lea como un único movimiento y no como dos saltos.
     ------------------------------------------------------------------------- */

  /** Aire contra los bordes del área visible al recolocar. */
  const ANCHOR_MARGIN = 24;

  /** Debe cuadrar con la transición de `.brn-faq__answer` en faq.css, con holgura. */
  const ANCHOR_SETTLE_MS = 450;

  /** El primer ancestro que scrollea de verdad; `null` si scrollea la ventana. */
  function scrollParent(el) {
    for (let node = el.parentElement; node; node = node.parentElement) {
      const overflowY = getComputedStyle(node).overflowY;
      const scrolls = overflowY === "auto" || overflowY === "scroll";
      if (scrolls && node.scrollHeight > node.clientHeight) return node;
    }

    return null;
  }

  /**
   * Recoloca `item` si, con el alto que va a tener, se saldría por abajo.
   *
   * @param {HTMLElement} item  La pregunta que se abre.
   * @param {number} grow       Lo que va a crecer (0 si ya ha terminado).
   * @param {number} shrinkAbove Lo que va a encoger por encima: la respuesta de
   *                             la pregunta que el acordeón cierra a la vez.
   */
  function anchorItem(item, grow, shrinkAbove) {
    const scroller = scrollParent(item);
    const view = scroller
      ? scroller.getBoundingClientRect()
      : { top: 0, height: window.innerHeight };
    const viewBottom = view.top + view.height;

    const rect = item.getBoundingClientRect();
    const top = rect.top - shrinkAbove;
    const height = rect.height + grow;

    // Cabe entero donde está: no se toca el scroll.
    if (top + height <= viewBottom - ANCHOR_MARGIN) return;

    // Se centra si cabe; si no cabe, manda la pregunta, que es lo que hay que
    // poder leer mientras se recorre la respuesta.
    const fits = height <= view.height - ANCHOR_MARGIN * 2;
    const desired = fits
      ? view.top + (view.height - height) / 2
      : view.top + ANCHOR_MARGIN;

    const delta = top - desired;
    if (Math.abs(delta) < 1) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    (scroller || window).scrollBy({
      top: delta,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }

  /**
   * Arranca el anclado con la geometría prevista y lo corrige al terminar: el
   * documento todavía no ha crecido cuando empieza la expansión, así que el
   * navegador recorta ese primer desplazamiento al máximo disponible entonces.
   */
  function scheduleAnchor(item, shrinkAbove) {
    const answer = item.querySelector(".brn-faq__answer");
    const inner = item.querySelector(".brn-faq__answer-inner");
    if (!answer || !inner) return;

    anchorItem(item, inner.offsetHeight, shrinkAbove);

    const settle = () => {
      clearTimeout(timer);
      answer.removeEventListener("transitionend", handleEnd);
      anchorItem(item, 0, 0);
    };

    const handleEnd = (event) => {
      if (event.propertyName === "grid-template-rows") settle();
    };

    answer.addEventListener("transitionend", handleEnd);
    const timer = setTimeout(settle, ANCHOR_SETTLE_MS);
  }

  // En captura, para medir el estado previo antes de que `collapsible-section`
  // conmute el acordeón desde su listener en burbuja.
  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest?.(".brn-faq__question");
      const item = trigger?.closest(".brn-faq__item");
      if (!item || !item.closest("[data-faq-category]")) return;

      // Al cerrar no hay contenido nuevo que enseñar, así que no se recoloca.
      if (item.dataset.open === "true") return;

      const closing = item
        .closest(".brn-faq__panel")
        ?.querySelector('.brn-faq__item[data-open="true"]');

      // Solo encoge por encima lo que se cierre por delante en el documento.
      const isAbove =
        closing &&
        !!(closing.compareDocumentPosition(item) & Node.DOCUMENT_POSITION_FOLLOWING);

      scheduleAnchor(
        item,
        isAbove ? closing.querySelector(".brn-faq__answer").getBoundingClientRect().height : 0,
      );
    },
    true,
  );
}
